import store from "@/store/store";
import Service from "@/service/api";
import { setItem, getItem, removeItem } from '@/utils/db';
import router from "../../../router/index.js";
import { clearActiveWorkspaceRole, resolveLandingPathForWorkspace } from '@/utils/role-dashboard';
import { getDefaultRole, getRoleLandingPath } from '@/config/workspace';
import { applyWorkspacePreferences, getPreferredLandingPage } from '@/utils/workspace-preferences';
import { showWarningAlert } from '@/utils/sweet-alert';

const TRUSTED_DEVICE_ID_KEY = 'trusted-device-id-v1';
const X_ACCESS_TOKEN_STORAGE_KEY = 'x-access-token';
const APP_AUTH_SYSTEM = process.env.VUE_APP_AUTH_SYSTEM || process.env.VUE_APP_PROJECT_APP_ID || 'mca';
const MCA_SCOPE_ERROR = 'account_not_in_mca_scope';
const MCA_SCOPE_DENIED_TITLE = 'Access not assigned';
const MCA_SCOPE_DENIED_MESSAGE = 'Your MFU account is not assigned to the MCA workspace yet. Please contact an administrator and ask them to invite you or add you to the Account Directory.';
let bootstrapPromise = null;

function withAuthSystem(payload) {
    return Object.assign({}, payload || {}, {
        system: APP_AUTH_SYSTEM,
        authSystem: APP_AUTH_SYSTEM
    });
}

function getOrCreateDeviceId() {
    if (typeof window === 'undefined' || !window.localStorage) {
        return '';
    }
    var existed = window.localStorage.getItem(TRUSTED_DEVICE_ID_KEY);
    if (existed && String(existed).trim()) {
        return String(existed).trim();
    }
    var randomPart = Math.random().toString(36).slice(2);
    var id = 'dev-' + Date.now() + '-' + randomPart;
    window.localStorage.setItem(TRUSTED_DEVICE_ID_KEY, id);
    return id;
}

function getTokenFromLocalStorage() {
    if (typeof window === 'undefined' || !window.localStorage) {
        return '';
    }
    const raw = window.localStorage.getItem(X_ACCESS_TOKEN_STORAGE_KEY);
    return raw && String(raw).trim() ? String(raw).trim() : '';
}

function determineRedirect(profile) {
    try {
        if (!profile || typeof profile !== 'object') return getRoleLandingPath(getDefaultRole());
        return resolveLandingPathForWorkspace(profile, getPreferredLandingPage(''));
    } catch (err) {
        return getRoleLandingPath(getDefaultRole());
    }
}

function setTokenToLocalStorage(token) {
    if (typeof window === 'undefined' || !window.localStorage) {
        return;
    }
    if (token && String(token).trim()) {
        window.localStorage.setItem(X_ACCESS_TOKEN_STORAGE_KEY, String(token).trim());
    } else {
        window.localStorage.removeItem(X_ACCESS_TOKEN_STORAGE_KEY);
    }
}

function clearPersistedSession() {
    removeItem('objs');
    setTokenToLocalStorage('');
}

function getBackendErrorCode(err) {
    const data = err && err.response && err.response.data ? err.response.data : {};
    return String(data.error || data.code || err && err.message || '').trim();
}

function getBackendErrorMessage(err) {
    const data = err && err.response && err.response.data ? err.response.data : {};
    return data.message ? String(data.message) : '';
}

function shouldRetryTwoFARequest(err) {
    if (!err || !err.response) return true;
    const status = Number(err.response.status);
    return status >= 500;
}

function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

const ServerModule = {
    namespaced: true,
    state: {
        authenticated: {
            isAuthen: false,
            isOAuth: false,
        },
        pendingToken: '',
        twoFAChallenge: null,
        isSignIn: true,
        is2FA: false,
        profile: null,
        message:[],
        bootstrappedToken: '',
    },

    modules: {

    },

    mutations: {
        authenticated(state, obj) {
            state.authenticated = Object.assign({}, state.authenticated, obj || {});
        },

        message(state, obj) {
            state.message = obj;
        },
        profile(state, obj) {
            state.profile = obj || null;
        },
        isSignIn(state, value) {
            state.isSignIn = !!value;
        },
        is2FA(state, value) {
            state.is2FA = !!value;
        },
        pendingToken(state, value) {
            state.pendingToken = value ? String(value) : '';
        },
        twoFAChallenge(state, value) {
            state.twoFAChallenge = value && typeof value === 'object'
                ? Object.assign({}, value)
                : null;
        },
        bootstrappedToken(state, value) {
            state.bootstrappedToken = value ? String(value) : '';
        }

    },

    actions: {
        async bootstrapSession({ commit, dispatch, state }) {
            const existingToken = String(store.state.XAccessToken || '').trim();
            const existingProfile = store.getters['auth/profile'] || null;
            const existingAuth = store.getters['auth/authenticated'] || {};
            if (
                existingToken &&
                state.bootstrappedToken === existingToken &&
                existingProfile &&
                existingAuth.isAuthen
            ) {
                return existingProfile;
            }

            if (bootstrapPromise) {
                return bootstrapPromise;
            }

            bootstrapPromise = (async () => {
            try {
                const objs = await getItem('objs');
                const tokenFromDb = objs && objs.xAccessToken ? String(objs.xAccessToken).trim() : '';
                const tokenFromLocal = getTokenFromLocalStorage();
                const token = tokenFromDb || tokenFromLocal;
                if (!token) {
                    commit('authenticated', { isAuthen: false, isOAuth: false });
                    commit('profile', null);
                    commit('isSignIn', true);
                    commit('is2FA', false);
                    commit('pendingToken', '');
                    commit('twoFAChallenge', null);
                    commit('bootstrappedToken', '');
                    return null;
                }

                if (token) {
                    store.commit('set', ['XAccessToken', token]);
                    setTokenToLocalStorage(token);
                    if (!tokenFromDb) {
                        await setItem('objs', { xAccessToken: token });
                    }
                    if (
                        state.bootstrappedToken === token &&
                        existingProfile &&
                        existingAuth.isAuthen
                    ) {
                        commit('authenticated', { isAuthen: true, isOAuth: true });
                        commit('isSignIn', false);
                        return existingProfile;
                    }

                    try {
                        const meRes = await Service.authenticated('me', {}, {});
                        const me = meRes && meRes.data ? meRes.data.data : null;
                        commit('authenticated', { isAuthen: true, isOAuth: true });
                        commit('isSignIn', false);
                        commit('profile', me);
                        commit('bootstrappedToken', token);
                        applyWorkspacePreferences({ store });
                        return me;
                    } catch (err) {
                        clearPersistedSession();
                        store.commit('set', ['XAccessToken', '']);
                        commit('authenticated', { isAuthen: false, isOAuth: false });
                        commit('profile', null);
                        commit('isSignIn', true);
                        commit('is2FA', false);
                        commit('pendingToken', '');
                        commit('twoFAChallenge', null);
                        commit('bootstrappedToken', '');
                        return null;
                    }
                }
            } catch (err) {
                commit('authenticated', { isAuthen: false, isOAuth: false });
                commit('profile', null);
                commit('isSignIn', true);
                commit('is2FA', false);
                commit('pendingToken', '');
                commit('twoFAChallenge', null);
                commit('bootstrappedToken', '');
                return null;
            } finally {
                bootstrapPromise = null;
            }
            return null;
            })();

            return bootstrapPromise;
        },
        message({commit}, data) {

            Service.authenticated('message', data, {})
                .then((response) => {
                    store.commit("auth/message", response.data.data)
                }).catch((err) => {

            });
        },

        createMessage({commit}, data) {

            Service.authenticated('create-message', data, {})
                .then((response) => {
                    store.commit("auth/message", response.data.data);
                    store.dispatch("auth/message",{});
                }).catch((err) => {
            });
        },


        updateMessage({commit}, data) {
            Service.authenticated('update-message', data, {})
                .then((response) => {
                    store.commit("auth/message", response.data.data);
                    store.dispatch("auth/message",{});
                }).catch((err) => {
            });
        },

        removeMessage({commit}, data) {
            Service.authenticated('remove-message', data)
                .then((response) => {
                    store.commit("auth/message", response.data.data);
                    store.dispatch("auth/message",{});
                }).catch((err) => {
            });


        },


        async signIn({commit, dispatch}, data) {
            store.commit("dialog/loading", true);
            try {
                const payload = withAuthSystem(data || {});
                payload.deviceId = getOrCreateDeviceId();

                const response = await Service.authenticated('signin', payload, {});
                const objs = response && response.data ? response.data.data : null;
                const token = objs && objs.xAccessToken ? String(objs.xAccessToken) : '';
                if (!token) {
                    throw new Error('missing_token');
                }
                const require2FA = !(objs && objs.require2FA === false);

                store.commit('set', ['XAccessToken', token]);
                commit('pendingToken', require2FA ? token : '');
                commit('authenticated', { isAuthen: false, isOAuth: false });
                commit('profile', null);
                commit('isSignIn', false);
                commit('is2FA', false);
                commit('twoFAChallenge', null);

                if (!require2FA) {
                    await setItem('objs', { xAccessToken: token });
                    setTokenToLocalStorage(token);
                    const meRes = await Service.authenticated('me', {}, {});
                    const me = meRes && meRes.data ? meRes.data.data : null;
                    commit('profile', me);
                    commit('authenticated', { isAuthen: true, isOAuth: true });
                    commit('isSignIn', false);
                    commit('is2FA', false);
                    commit('pendingToken', '');
                    commit('twoFAChallenge', null);
                    commit('bootstrappedToken', token);
                    applyWorkspacePreferences({ store });
                    if (router && typeof router.push === 'function') {
                        const redirectPath = determineRedirect(me);
                        if (!router.currentRoute || router.currentRoute.path !== redirectPath) {
                            router.push(redirectPath).catch(() => {});
                        }
                    }
                    return;
                }

                clearPersistedSession();

                try {
                    await dispatch('twofa', {});
                } catch (err) {
                    store.commit('set', ['XAccessToken', '']);
                    clearPersistedSession();
                    commit('pendingToken', '');
                    commit('twoFAChallenge', null);
                    commit('authenticated', { isAuthen: false, isOAuth: false });
                    commit('profile', null);
                    commit('isSignIn', true);
                    commit('is2FA', false);
                    store.commit('dialog/showError', {
                        title: "Authentication Error",
                        message: "Unable to send verification code. Please try again.",
                        code: "AUTH_2FA_REQUEST_FAILED",
                        number: "1",
                        status: true
                    })
                    return;
                }
            } catch (err) {
                store.commit("dialog/loading",false)
                const backendCode = getBackendErrorCode(err) || "AUTH_SIGNIN_FAILED";
                const backendMessage = getBackendErrorMessage(err) || "Sign in failed. Please try again.";
                if (backendCode === MCA_SCOPE_ERROR) {
                    try {
                        await showWarningAlert(MCA_SCOPE_DENIED_MESSAGE, MCA_SCOPE_DENIED_TITLE);
                    } catch (alertError) {
                        store.commit('dialog/showError', {
                            title: MCA_SCOPE_DENIED_TITLE,
                            message: MCA_SCOPE_DENIED_MESSAGE,
                            code: MCA_SCOPE_ERROR,
                            number: "1",
                            status: true,
                            color: 'warning'
                        })
                    }
                    return;
                }
                store.commit('dialog/showError', {
                    title: "Authentication Error",
                    message: backendMessage,
                    code: backendCode,
                    number: "1",
                    status: true
                })
            } finally {
                store.commit("dialog/loading",false)
            }
        },

        async twofa({ commit }, data) {
            let response = null;
            let lastError = null;
            for (let attempt = 0; attempt < 2; attempt += 1) {
                try {
                    response = await Service.authenticated('twofa-request', withAuthSystem(data || {}), {});
                    break;
                } catch (err) {
                    lastError = err;
                    if (attempt >= 1 || !shouldRetryTwoFARequest(err)) {
                        throw err;
                    }
                    await delay(700);
                }
            }
            if (!response && lastError) {
                throw lastError;
            }
            const info = response && response.data ? (response.data.data || null) : null;
            commit('twoFAChallenge', info);
            commit('is2FA', true);
            return info;
        },

        async twofaSend({ commit, state }, data) {
            await Service.authenticated('twofa-verify', withAuthSystem(data || {}), {});
            if (!state.pendingToken) {
                throw new Error('missing_pending_token');
            }
            const verifiedToken = String(state.pendingToken);
            store.commit('set', ['XAccessToken', verifiedToken]);
            await setItem('objs', { xAccessToken: verifiedToken });
            setTokenToLocalStorage(verifiedToken);
            const meRes = await Service.authenticated('me', {}, {});
            const me = meRes && meRes.data ? meRes.data.data : null;

            commit('profile', me);
            commit('isSignIn', false);
            commit('is2FA', false);
            commit('twoFAChallenge', null);
            commit('bootstrappedToken', verifiedToken);
            applyWorkspacePreferences({ store });
            return true;
        },

        async trustDevice() {
            await Service.authenticated('trust-device', withAuthSystem({ deviceId: getOrCreateDeviceId() }), {});
            return true;
        },

        async completeSignInFlow({ commit, dispatch }, payload) {
            var trustDevice = !!(payload && payload.trustDevice);
            if (trustDevice) {
                try {
                    await dispatch('trustDevice');
                } catch (err) {
                    console.error('trust-device error (non-blocking):', err);
                }
            }
            commit('authenticated', { isAuthen: true, isOAuth: true });
            commit('pendingToken', '');
            if (router && typeof router.push === 'function') {
                const profile = store.getters['auth/profile'] || null;
                const redirectPath = determineRedirect(profile);
                if (!router.currentRoute || router.currentRoute.path !== redirectPath) {
                    router.push(redirectPath).catch(() => {});
                }
            }
            return true;
        },

        async signOut({ commit, dispatch }, data) {
            try {
                // Call logout endpoint on backend
                await Service.authenticated('logout', withAuthSystem({}), {});
            } catch (err) {
                console.error('Logout error (non-blocking):', err);
            }

            // Clear local storage and state
            clearPersistedSession();
            store.commit('set', ['XAccessToken', '']);
            clearActiveWorkspaceRole();
            commit('authenticated', { isAuthen: false, isOAuth: false });
            commit('profile', null);
            commit('isSignIn', true);
            commit('is2FA', false);
            commit('pendingToken', '');
            commit('twoFAChallenge', null);
            commit('bootstrappedToken', '');
            store.commit('security/reset');
            if (router && typeof router.push === 'function') {
                if (!router.currentRoute || router.currentRoute.path !== '/pages/login') {
                    router.push('/pages/login').catch(() => {});
                }
            }
        },

    },

    getters: {

        message(state) {
            return state.message;
        },

        authenticated(state) {
            return state.authenticated;
        },
        profile(state) {
            return state.profile;
        },
        isSignIn(state) {
            return state.isSignIn;
        },
        is2FA(state) {
            return state.is2FA;
        },
        pendingToken(state) {
            return state.pendingToken;
        },
        twoFAChallenge(state) {
            return state.twoFAChallenge;
        }
    },
};

export default ServerModule;
